import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthRequired } from '../../contexts/AuthRequiredContext';
import venueApiService from '../../services/venueApi';
import { saveAuthReturnTo } from '../../utils/authReturnTo';
import { resolveVenueIdFromPathParam } from '../../utils/venueSlug';
import './AuthGate.css';

const isLocalhost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/**
 * Gates access to search, venue details, and booking pages.
 * When not logged in (and not on localhost): redirects to previous page,
 * shows sign-in modal there. Does NOT load the protected page or hit backend.
 *
 * Pass enforce={false} (e.g. when discovery gate is off via REACT_APP_AUTH_GATE_DISCOVERY_ENFORCED) to allow anonymous
 * access without blur, redirect, or modal.
 */
const AuthGate = ({ children, mode = 'redirect', allowNonPartnerVenueById = false, enforce = true }) => {
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const { venueId } = useParams();
  const { triggerAuthModal, closeAuthModal } = useAuthRequired();
  const hasRedirected = useRef(false);
  const [partnerCheckLoading, setPartnerCheckLoading] = useState(allowNonPartnerVenueById);
  const [shouldGateLoggedOut, setShouldGateLoggedOut] = useState(true);

  const bypassAuth = isLocalhost();

  useEffect(() => {
    if (!enforce) return;

    let cancelled = false;

    const checkPartnerByVenueId = async () => {
      if (!allowNonPartnerVenueById || !venueId || loading || bypassAuth || currentUser) {
        setPartnerCheckLoading(false);
        setShouldGateLoggedOut(true);
        return;
      }

      try {
        setPartnerCheckLoading(true);
        const resolvedVenueId = await resolveVenueIdFromPathParam(venueId, venueApiService);
        if (!resolvedVenueId) {
          setShouldGateLoggedOut(true);
          return;
        }
        const venue = await venueApiService.getVenueById(resolvedVenueId);
        if (cancelled) return;
        // Only partner venues should bypass auth blur/login gating.
        const isPartnerVenue = venue?.is_partner === true || venue?.isPartner === true;
        const gateDecision = !isPartnerVenue;
        setShouldGateLoggedOut(gateDecision);
      } catch (error) {
        if (cancelled) return;
        // Fail secure: if venue check fails, keep gating enabled.
        setShouldGateLoggedOut(true);
      } finally {
        if (!cancelled) setPartnerCheckLoading(false);
      }
    };

    checkPartnerByVenueId();

    return () => {
      cancelled = true;
    };
  }, [enforce, allowNonPartnerVenueById, venueId, loading, bypassAuth, currentUser]);

  useEffect(() => {
    if (!enforce) return;

    // Ensure any previously opened auth modal is dismissed when gating is bypassed.
    if (bypassAuth || currentUser || !shouldGateLoggedOut) {
      closeAuthModal();
      hasRedirected.current = false;
    }
  }, [enforce, bypassAuth, currentUser, shouldGateLoggedOut, closeAuthModal]);

  useEffect(() => {
    if (!enforce) return;

    if (loading || partnerCheckLoading || bypassAuth || currentUser || !shouldGateLoggedOut) return;
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    const returnPath = `${window.location.pathname}${window.location.search}`;
    saveAuthReturnTo(returnPath);
    triggerAuthModal();

    const keepCurrentPath =
      window.location.pathname === '/messages' ||
      window.location.pathname === '/profile' ||
      window.location.pathname.startsWith('/profile/');

    if (mode === 'redirect' && !keepCurrentPath) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [
    enforce,
    loading,
    partnerCheckLoading,
    currentUser,
    bypassAuth,
    shouldGateLoggedOut,
    triggerAuthModal,
    navigate,
    mode,
  ]);

  if (!enforce) {
    if (loading) {
      return (
        <div className="auth-gate-loading">
          <div className="auth-gate-spinner" />
          <p>Loading...</p>
        </div>
      );
    }
    return children;
  }

  if (loading || partnerCheckLoading) {
    return (
      <div className="auth-gate-loading">
        <div className="auth-gate-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (bypassAuth || currentUser || !shouldGateLoggedOut) {
    return children;
  }

  const keepCurrentPath =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/messages' ||
      window.location.pathname === '/profile' ||
      window.location.pathname.startsWith('/profile/'));

  if (keepCurrentPath) {
    return (
      <div className="auth-gate-loading">
        <div className="auth-gate-spinner" />
        <p>Please sign in to continue...</p>
      </div>
    );
  }

  if (mode === 'blur') {
    return (
      <div className="auth-gate-blur-wrap" aria-hidden="true">
        <div className="auth-gate-blur-content">{children}</div>
        <div className="auth-gate-blur-overlay" />
      </div>
    );
  }

  return null;
};

export default AuthGate;
